import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { type ComponentProps, useState } from 'react'

import { Card, Loader } from '@/components/ui'
import { useProject } from '@/modules/projects'
import { isProjectProcessing } from '@/modules/projects/status'
import { cn } from '@/utils'

// The edge tucks this far under the card so its top is hidden behind the card's rounded corners.
// Only the part past it shows below the card.
const TUCK_HEIGHT = 8
const EDGE_HEIGHT = 5
// One line of text-xs with py-1. Fixed so the height never animates through `auto`, which
// makes motion measure and snap to the full size before shrinking.
const NOTE_HEIGHT = 24

// A Card with a thin striped edge hanging under it while the project extracts. Hovering or
// focusing the card grows the edge into a one-line note; the tabs strip carries the full sentence.
export const ProcessingCard = ({
  className,
  ...props
}: ComponentProps<typeof Card>) => {
  const project = useProject()
  const reduceMotion = useReducedMotion()
  const [isRevealed, setIsRevealed] = useState(false)
  const isProcessing = isProjectProcessing(project)
  const duration = reduceMotion ? 0 : 0.2

  return (
    <div
      // Raised while revealed so the note paints over whatever sits below the card.
      className={cn('relative flex flex-col mb-2', isRevealed && 'z-20')}
      onMouseEnter={() => setIsRevealed(true)}
      onMouseLeave={() => setIsRevealed(false)}
      onFocus={() => setIsRevealed(true)}
      onBlur={() => setIsRevealed(false)}
    >
      <Card className={cn('relative z-10 flex-1', className)} {...props} />
      <AnimatePresence initial={false}>
        {isProcessing && (
          // Anchored to the wrapper's bottom and out of flow, so nothing below the card ever moves.
          <motion.div
            role="status"
            initial={{ height: TUCK_HEIGHT }}
            animate={{
              height: TUCK_HEIGHT + (isRevealed ? NOTE_HEIGHT : EDGE_HEIGHT),
            }}
            exit={{ height: TUCK_HEIGHT }}
            transition={{ duration, ease: 'easeOut' }}
            style={{
              top: `calc(100% - ${TUCK_HEIGHT}px)`,
              paddingTop: TUCK_HEIGHT,
            }}
            className="border border-yellow-700/30 absolute inset-x-0 overflow-hidden rounded-b-lg bg-amber-50"
          >
            <motion.div
              aria-hidden
              animate={{ opacity: isRevealed ? 0 : 1 }}
              transition={{ duration }}
              className="absolute inset-0 bg-barberpole-over-amber-200"
            />
            <motion.div
              aria-hidden
              animate={{ opacity: isRevealed ? 1 : 0 }}
              transition={{ duration }}
              className="absolute inset-0 bg-barberpole-over-amber-200/20"
            />
            <motion.div
              animate={{ opacity: isRevealed ? 1 : 0 }}
              transition={{ duration }}
              style={{ height: NOTE_HEIGHT }}
              className="relative flex items-center gap-1.5 px-3 pb-1 font-medium text-xs text-yellow-800"
            >
              <Loader className="text-yellow-700" />
              Still processing
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
